import { checkPathContains, checkValidPath, parsePath } from "../../utilities/string";
import {
  IClientTarget,
  ISiteTarget,
  ITenantTarget,
  isSiteTarget,
  isTenantTarget,
} from "../../typings/generics";
import {
  DIVISIONS_COLLECTION,
  IMPORT_STORAGE_FOLDER,
  ORGANIZATIONS_COLLECTION,
  SITE_COLLECTION,
  TENANTS_COLLECTION,
} from "./constants";

export function storagePath(target: IClientTarget | ISiteTarget | ITenantTarget): string {
  if (isSiteTarget(target)) {
    return `${SITE_COLLECTION}/${target.siteId}`;
  } else if (isTenantTarget(target)) {
    const { tenantId, divisionId } = target;
    if (divisionId) {
      return `${TENANTS_COLLECTION}/${tenantId}/${DIVISIONS_COLLECTION}/${divisionId}`;
    }
    return `${TENANTS_COLLECTION}/${tenantId}`;
  } else {
    const { orgId, tenantId, divisionId } = target;
    if (divisionId && tenantId) {
      return `${ORGANIZATIONS_COLLECTION}/${orgId}/${TENANTS_COLLECTION}/${tenantId}/${DIVISIONS_COLLECTION}/${divisionId}`;
    }

    if (tenantId) {
      return `${ORGANIZATIONS_COLLECTION}/${orgId}/${TENANTS_COLLECTION}/${tenantId}`;
    }
    return `${ORGANIZATIONS_COLLECTION}/${orgId}`;
  }
}

export function checkValidStoragePath(
  tst: string,
  postfix?: string,
): IClientTarget | ISiteTarget | ITenantTarget | undefined {
  const pf = postfix ? "/" + postfix : "";
  const sitePath = `${storagePath({ siteId: "{siteId}" })}${pf}`;

  const rootTenantPath = `${storagePath({ tenantId: "{tenantId}" })}${pf}`;
  const rootTenantDivisionPath = `${storagePath({
    tenantId: "{tenantId}",
    divisionId: "{divisionId}",
  })}${pf}`;

  const orgPath = `${storagePath({ orgId: "{orgId}" })}${pf}`;
  const tenantPath = `${storagePath({
    orgId: "{orgId}",
    tenantId: "{tenantId}",
  })}${pf}`;
  const divisionPath = `${storagePath({
    orgId: "{orgId}",
    tenantId: "{tenantId}",
    divisionId: "{divisionId}",
  })}${pf}`;

  for (const path of [
    divisionPath,
    tenantPath,
    orgPath,
    sitePath,
    rootTenantPath,
    rootTenantDivisionPath,
  ]) {
    if (checkValidPath(tst, path)) {
      const { orgId, tenantId, divisionId, siteId } = parsePath(tst, path);
      return { orgId, tenantId, divisionId, siteId } as IClientTarget | ISiteTarget | ITenantTarget;
    }
  }
  return undefined;
}

/**
 * different that checkValidStoragePath. It doesn't need to have any postfix, it just needs to start with the right path
 *
 * @param path
 * @returns
 */
export function getTargetFromStoragePath(
  path: string,
): IClientTarget | ISiteTarget | ITenantTarget | undefined {
  const sitePath = `${storagePath({ siteId: "{siteId}" })}`;

  const rootTenantPath = `${storagePath({ tenantId: "{tenantId}" })}`;
  const rootTenantDivisionPath = `${storagePath({
    tenantId: "{tenantId}",
    divisionId: "{divisionId}",
  })}`;

  const orgPath = `${storagePath({ orgId: "{orgId}" })}`;
  const tenantPath = `${storagePath({
    orgId: "{orgId}",
    tenantId: "{tenantId}",
  })}`;
  const divisionPath = `${storagePath({
    orgId: "{orgId}",
    tenantId: "{tenantId}",
    divisionId: "{divisionId}",
  })}`;

  for (const _path of [
    divisionPath,
    tenantPath,
    orgPath,
    sitePath,
    rootTenantPath,
    rootTenantDivisionPath,
  ]) {
    if (checkPathContains(path, _path)) {
      const { orgId, tenantId, divisionId, siteId } = parsePath(path, _path);
      return { orgId, tenantId, divisionId, siteId } as IClientTarget | ISiteTarget | ITenantTarget;
    }
  }
  return undefined;
}

export const IMPORT_SITE_STORAGE_PATH = `${SITE_COLLECTION}/{siteId}/${IMPORT_STORAGE_FOLDER}/{importId}/{fileName}`;

export const IMPORT_ROOT_TENANT_STORAGE_PATH = `${TENANTS_COLLECTION}/{tenantId}/${IMPORT_STORAGE_FOLDER}/{importId}/{fileName}`;

export const IMPORT_ORG_STORAGE_PATH = `${ORGANIZATIONS_COLLECTION}/{orgId}/${IMPORT_STORAGE_FOLDER}/{importId}/{fileName}`;
export const IMPORT_TENANT_STORAGE_PATH = `${ORGANIZATIONS_COLLECTION}/{orgId}/${TENANTS_COLLECTION}/{tenantId}/${IMPORT_STORAGE_FOLDER}/{importId}/{fileName}`;
